/**
 * Offers the search can never choose.
 *
 * ---------------------------------------------------------------------------
 * THE MISTAKE THIS CATCHES
 * ---------------------------------------------------------------------------
 *
 * A length-of-stay offer only exists to be cheaper than buying the nights one at
 * a time. Price it above that and it is not a bad deal — it is INVISIBLE: the
 * engine tiles the stay from whatever is cheapest, the offer never appears in any
 * quote, and the price list keeps a row that has no effect on any guest.
 *
 * It looks exactly like a working offer from every screen. It has a name, a
 * window, a price, and it saves nobody anything. The season's two-week rate that
 * costs 8% more than fourteen single nights reads as a discount to everyone in
 * the office and is a decoration in the file.
 *
 * The compiler cannot see this, because nothing about such a rate is malformed.
 * It is only visible by ASKING THE ENGINE — price a stay the offer was written
 * for, and see whether the answer used it.
 *
 * ---------------------------------------------------------------------------
 * WHY ONLY LENGTH OFFERS AND PACKAGES
 * ---------------------------------------------------------------------------
 *
 * A nightly band that costs more than the band around it is NOT a mistake: bands
 * layer narrowest-first, so a dear peak week deliberately beats the cheap season
 * band underneath it. Applying a "must be cheaper" rule to nightly rates would
 * report every peak in the season as broken.
 *
 * Ladders and arrangements are the ones that compete on total cost, so they are
 * the ones where losing every time means something is wrong.
 *
 * ---------------------------------------------------------------------------
 * WHY SAMPLES AND NOT A PROOF
 * ---------------------------------------------------------------------------
 *
 * "Is this rate part of the cheapest tiling for SOME stay" is a search over every
 * arrival, length and party size, and the honest cheap version is to try the
 * stays the rate was actually written for: its own length, arriving on the days
 * it allows, spread across its own window. A rate that loses all of those is not
 * being used by anybody.
 *
 * The diagnostic therefore says "never chosen for the stays it was written for",
 * which is what was tested, rather than "unreachable", which was not.
 */

import type { Diagnostic, Programme } from './authoring';
import { rateIdOf } from './pieces.js';
import { compileProgramme, WEEKDAY_ORDER } from './authoring';
import { priceStay } from './engine';
import type { PieceSet, Quote } from './types';

const MS = 86_400_000;
const dayOf = (iso: string): number => Math.round(Date.parse(`${iso}T00:00:00Z`) / MS);

/** Roles that have to win on price to exist at all. */
const COMPETES_ON_PRICE = new Set(['ladder', 'arrangement']);

/** At most this many stays tried per rate. Enough to cover a season, cheap enough to run on every keystroke. */
const MAX_SAMPLES = 24;

/**
 * A compiled piece keeps its rate's id, with a suffix when one rate compiles to
 * several. Both forms have to map back to the rate the operator sees.
 */
// One reduction, in the package that mints the ids. See `pieces.ts` for the
// two suffixes and for what this local copy used to get wrong.

/** The arrivals worth trying for one rate: inside its window, on days it allows. */
function samplesFor(
  from: number, until: number, nights: number, allowed: Set<number> | null,
): number[] {
  const last = until - nights;
  if (last < from) return [];

  const span = last - from + 1;
  const step = Math.max(1, Math.ceil(span / MAX_SAMPLES));
  const out: number[] = [];
  for (let d = from; d <= last; d += step) {
    // Weekday of a day number: 1970-01-01 was a Thursday, so index 3 in a
    // Monday-first week.
    if (allowed && !allowed.has(((d % 7) + 7 + 3) % 7)) continue;
    out.push(d);
  }
  // A rate that only starts on Saturdays can have every sampled day skipped by
  // the step. Falling back to the first allowed day keeps it from being reported
  // as never chosen when it was never tried.
  if (out.length === 0 && allowed) {
    for (let d = from; d <= last; d++) {
      if (allowed.has(((d % 7) + 7 + 3) % 7)) { out.push(d); break; }
    }
  }
  return out;
}

export interface ReachabilityOptions {
  /** The party to price for. Defaults to what the card says is included. */
  quantities?: Record<string, number>;
  set?: PieceSet;
}

/**
 * Diagnostics for offers no stay would ever be given.
 *
 * Warnings, never errors: an offer nobody can reach is a waste and not a
 * falsehood, and a park mid-way through writing next season's card would
 * otherwise be told its half-finished work is broken.
 */
export function findUnreachableRates(
  programme: Programme, options: ReachabilityOptions = {},
): Diagnostic[] {
  let set = options.set;
  if (!set) {
    const compiled = compileProgramme(programme);
    if (!compiled.ok) return [];
    set = compiled.set;
  }

  // A party the card actually accommodates. Pricing for nobody makes every
  // per-person offer look identical to every other.
  const quantities = options.quantities ?? defaultParty(programme);

  const out: Diagnostic[] = [];
  programme.rates.forEach((rate, i) => {
    if (!COMPETES_ON_PRICE.has(rate.role ?? '')) return;
    if (rate.availability === 'closed') return;

    /**
     * Both kinds of changeover day, and the difference matters here.
     *
     * `arriveOn` restricts where a BLOCK may be placed; `stayArriveOn` restricts
     * the whole stay. Sampling only the first tried Sundays for a Saturday-only
     * week, found it unused, and reported a correctly-priced offer as too dear —
     * the wrong answer with a confident number attached to it.
     */
    const allowed = allowedDays(rate.arriveOn, rate.stayArriveOn);

    // Placement windows, when set, are the only places it may go.
    const windows = rate.placementWindows?.length
      ? rate.placementWindows
      : [rate.active];

    let tried = 0;
    let chosen = false;
    let cheapest: number | null = null;
    let ownPrice: number | null = null;

    for (const w of windows) {
      if (chosen) break;
      for (const arrival of samplesFor(dayOf(w.from), dayOf(w.until), rate.nights, allowed)) {
        tried++;
        let quote: Quote;
        try {
          quote = priceStay(set, { arrival, nights: rate.nights, quantities });
        } catch {
          // A stay this card cannot price at all says nothing about this rate.
          continue;
        }
        if (quote.status === 'unavailable') continue;
        if (quote.lines.some((l) => rateIdOf(l.pieceId) === rate.id)) { chosen = true; break; }
        // The ACCOMMODATION subtotal, not the quote total: the total carries
        // tourist tax and cleaning, which this rate would owe as well. Comparing
        // a rate's price against a total that includes them overstates the gap
        // by the tax, and a diagnostic that quotes a wrong number is worse than
        // one that quotes none.
        const nightsCost = quote.lines.reduce((sum, l) => sum + l.price, 0);
        if (cheapest === null || nightsCost < cheapest) {
          cheapest = nightsCost;
          ownPrice = ownCost(rate, quantities, programme);
        }
      }
    }

    if (chosen) return;

    if (tried === 0) {
      /**
       * Not too dear — unplaceable. A different fault with a different fix, and
       * conflating them sends somebody to change a price that was never the
       * problem.
       *
       * The case that found it: a Meivakantieweek that may only start on a
       * Saturday, in a window beginning the day AFTER the last Saturday it could
       * have used. Nothing about it is malformed, it prices correctly in every
       * test, and no guest can ever be given it.
       */
      out.push({
        severity: 'warning',
        path: `rates[${i}]`,
        code: 'never-placeable',
        message: `"${rate.label}" can never be placed: no stay of ${rate.nights} nights `
          + 'fits inside its window on a day it is allowed to start. '
          + 'Widen the window, or allow another starting day.',
      });
      return;
    }

    // The number is the point. "Never chosen" invites an argument; "costs EUR 51
    // more than the nights it covers" ends one.
    const excess = ownPrice != null && cheapest != null ? ownPrice - cheapest : null;
    out.push({
      severity: 'warning',
      path: `rates[${i}]`,
      code: 'never-chosen',
      message: excess != null && excess > 0
        ? `"${rate.label}" is never chosen: over ${tried} stays it was written for, `
          + `the same nights always cost less bought separately — ${excess.toFixed(2)} less at best. `
          + 'A length offer priced above the nights it covers is invisible to guests.'
        : `"${rate.label}" is never chosen: over ${tried} stays it was written for, `
          + 'another combination was always at least as cheap.',
    });
  });
  return out;
}

/**
 * What this rate would charge for the party being priced.
 *
 * Mirrors the authoring shape rather than the compiled one: `base` plus the
 * per-unit surcharges on whoever is not already covered by the base. Comparing
 * bases alone would call a rate cheap because its surcharges are where the money
 * is, which is exactly how a per-person card is written.
 */
function ownCost(
  rate: Programme['rates'][number],
  quantities: Record<string, number>,
  programme?: Programme,
): number {
  let total = rate.price?.base ?? 0;
  for (const [key, amount] of Object.entries(rate.price?.perUnit ?? {})) {
    const included = programme?.quantities?.find((q) => q.key === key)?.includedInBase ?? 0;
    const chargeable = Math.max(0, (quantities[key] ?? 0) - included);
    total += amount * chargeable * (rate.price?.perUnitIsPerNight ? rate.nights : 1);
  }
  return total;
}

/** The weekdays a placement may start on, as day-of-week indexes, or null for any. */
function allowedDays(
  arriveOn: readonly string[] | undefined, stayArriveOn: readonly string[] | undefined,
): Set<number> | null {
  const toSet = (days: readonly string[] | undefined) => (days?.length
    ? new Set(days.map((d) => WEEKDAY_ORDER.indexOf(d as never)).filter((x) => x >= 0))
    : null);
  const a = toSet(arriveOn);
  const b = toSet(stayArriveOn);
  if (!a) return b;
  if (!b) return a;
  // Both set: only days that satisfy both, which can legitimately be none — and
  // that is exactly the sort of rate this file exists to point at.
  return new Set([...a].filter((d) => b.has(d)));
}

/** Two adults, or whatever the card calls its first person category. */
function defaultParty(programme: Programme): Record<string, number> {
  const person = programme.quantities?.find((q) => (q.channel ?? 'adult') === 'adult');
  const included = programme.occupancy?.included ?? 2;
  return person ? { [person.key]: Math.max(1, included) } : {};
}
